package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.widthIn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.ativos.Ativo
import br.com.htms.inspeccaocampo.data.ativos.AtivoStatus
import br.com.htms.inspeccaocampo.data.ativos.AtivosDados
import br.com.htms.inspeccaocampo.data.ativos.Movimentacao
import br.com.htms.inspeccaocampo.data.ativos.MovimentacaoTipo
import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.ui.kit.CoresDash
import br.com.htms.inspeccaocampo.ui.kit.DashDatas
import br.com.htms.inspeccaocampo.ui.kit.FatiaDash
import br.com.htms.inspeccaocampo.ui.kit.FiltroPeriodo
import br.com.htms.inspeccaocampo.ui.kit.FiltrosAtivos
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.kit.GradeKpis
import br.com.htms.inspeccaocampo.ui.kit.GraficoColunas
import br.com.htms.inspeccaocampo.ui.kit.GraficoRosca
import br.com.htms.inspeccaocampo.ui.kit.ItemPainel
import br.com.htms.inspeccaocampo.ui.kit.KpiDash
import br.com.htms.inspeccaocampo.ui.kit.PainelDashboard
import br.com.htms.inspeccaocampo.ui.kit.PaineisDoDash
import br.com.htms.inspeccaocampo.ui.kit.PeriodoDash
import br.com.htms.inspeccaocampo.ui.kit.RankingBarras
import br.com.htms.inspeccaocampo.ui.kit.Seletor
import br.com.htms.inspeccaocampo.ui.kit.SemDadosDash
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

private val CorStatusAtivo = mapOf(
    "EM_USO" to Color(0xFF1E7B3A),
    "DISPONIVEL" to Color(0xFF1E5AA8),
    "MANUTENCAO" to Color(0xFFD06A00),
    "DEVOLVIDO" to Color(0xFF8A94A6),
    "BAIXADO" to Color(0xFFC62828)
)

@Composable
fun DashboardAtivos(d: AtivosDados, carregando: Boolean = false, onAtualizar: (() -> Unit)? = null) {
    var cc by remember { mutableStateOf("") }
    var periodo by remember { mutableStateOf(PeriodoDash.M12) }
    var fStatus by remember { mutableStateOf<String?>(null) }
    var fCategoria by remember { mutableStateOf<String?>(null) }
    var fFaixa by remember { mutableStateOf<Int?>(null) }

    fun nomeCc(id: String) = d.centrosCusto.firstOrNull { it.id == id }?.rotulo ?: "Sem obra"
    fun categoria(a: Ativo) = a.categoria.ifBlank { "Sem categoria" }
    fun dataMov(m: Movimentacao) =
        DashDatas.normalizar(m.data).ifBlank { if (m.createdAt > 0) DashDatas.deMs(m.createdAt) else "" }

    val hoje = DashDatas.hoje()
    val iniPeriodo = DashDatas.inicio(periodo, hoje)
    fun noPeriodo(iso: String) = iso.isNotBlank() && iso <= hoje && (iniPeriodo == null || iso >= iniPeriodo)

    /** Data em que o ativo saiu (devolução/baixa); null enquanto está em posse. */
    val saidaDe = remember(d) {
        val porMov = d.movimentacoes
            .filter { it.tipo == "DEVOLUCAO" || it.tipo == "BAIXA" }
            .groupBy { it.ativoId }
            .mapValues { (_, v) -> v.maxOf { dataMov(it) } }
        d.ativos.filter { !it.emPosse }.associate { a ->
            a.id to (porMov[a.id]?.ifBlank { null } ?: if (a.updatedAt > 0) DashDatas.deMs(a.updatedAt) else "")
        }
    }
    val primeiraMov = remember(d) {
        d.movimentacoes.groupBy { it.ativoId }.mapValues { (_, v) -> v.map { dataMov(it) }.filter { it.isNotBlank() }.minOrNull().orEmpty() }
    }
    fun chegada(a: Ativo) = DashDatas.normalizar(a.dataChegada).ifBlank { primeiraMov[a.id].orEmpty() }

    /** Esteve sob responsabilidade da empresa em algum dia do período. */
    fun presente(a: Ativo): Boolean {
        val ch = chegada(a)
        if (ch.isNotBlank() && ch > hoje) return false
        val saida = saidaDe[a.id] ?: return true
        return saida.isBlank() || iniPeriodo == null || saida >= iniPeriodo
    }

    /** Dias de aluguel dentro do período (da chegada/início do período até a saída/hoje). */
    fun diasNoPeriodo(a: Ativo): Long {
        val ch = chegada(a)
        val ini = listOfNotNull(ch.ifBlank { null }, iniPeriodo).maxOrNull() ?: return 0
        val fim = saidaDe[a.id]?.ifBlank { null }?.let { minOf(it, hoje) } ?: hoje
        val di = DatasColab.diasDeIso(ini) ?: return 0
        val df = DatasColab.diasDeIso(fim) ?: return 0
        return (df - di + 1).coerceAtLeast(0)
    }
    fun custoNoPeriodo(a: Ativo): Double =
        if (!a.alugado) 0.0 else (a.valorDia ?: 0.0) * diasNoPeriodo(a)

    val presentes = d.ativos.filter { presente(it) }
    val daObra = presentes.filter { cc.isBlank() || it.centroCustoId == cc }
    val lista = daObra.filter { (fStatus == null || AtivoStatus.rotulo(it.status) == fStatus) && (fCategoria == null || categoria(it) == fCategoria) }
    val alugados = lista.filter { it.alugado }
    val custoPeriodo = alugados.sumOf { custoNoPeriodo(it) }
    val custoMesAtual = alugados.filter { it.emPosse }.sumOf { it.valorMes ?: 0.0 }
    val chegadas = lista.count { noPeriodo(chegada(it)) }
    val saidas = lista.count { saidaDe[it.id]?.let(::noPeriodo) == true }
    val solicNoPeriodo = d.solicitacoes.filter {
        (cc.isBlank() || it.centroCustoId == cc) &&
            noPeriodo(DashDatas.normalizar(it.data).ifBlank { if (it.createdAt > 0) DashDatas.deMs(it.createdAt) else "" })
    }
    val solicAbertas = solicNoPeriodo.count { it.status == "PENDENTE" || it.status == "AGUARDANDO_FROTAS" }

    val movs = d.movimentacoes
        .filter { it.tipo != "CADASTRO" && it.tipo != "EDICAO" }
        .filter { cc.isBlank() || it.paraCentroCustoId == cc || it.deCentroCustoId == cc }
        .filter { noPeriodo(dataMov(it)) }
    val faixas = remember(periodo, d) { DashDatas.faixas(periodo, d.movimentacoes.minOfOrNull { dataMov(it).ifBlank { "9999" } }) }
    val movsFiltradas = movs.filter { fFaixa == null || DashDatas.indice(faixas, dataMov(it)) == fFaixa }
    val coresMov = coresRanking(contagem(movs) { MovimentacaoTipo.rotulo(it.tipo) }.map { it.first })

    val filtrosAtivos = buildList {
        fStatus?.let { add("Status: $it" to { fStatus = null }) }
        fCategoria?.let { add("Categoria: $it" to { fCategoria = null }) }
        fFaixa?.let { i -> faixas.getOrNull(i)?.let { add("Movimentações: ${it.rotulo}" to { fFaixa = null }) } }
    }

    PainelDashboard(
        titulo = "Dashboard — Controle de Ativos",
        subtitulo = (if (cc.isBlank()) "Todas as obras" else nomeCc(cc)) +
            " · ${periodo.rotulo} · ${FmtDash.qtd(lista.size)} ativo(s) no período",
        carregando = carregando,
        onAtualizar = onAtualizar,
        camposCor = coresMov.keys.mapIndexed { i, k -> k to CoresDash.corLong(i) } + listOf(
            "Disponível" to 0xFF1B7F3B,
            "Em uso" to 0xFF1565C0,
            "Manutenção" to 0xFFE07B00,
            "Alugado" to 0xFFE65100
        ),
        filtros = {
            Box(Modifier.widthIn(min = 240.dp, max = 460.dp)) {
                Seletor(
                    "Obra / centro de custo",
                    listOf("" to "Todas as obras") + d.centrosCusto.map { it.id to it.rotulo + if (!it.ativo) " (inativa)" else "" },
                    cc, { cc = it }
                )
            }
            FiltroPeriodo(periodo, { periodo = it; fFaixa = null })
            FiltrosAtivos(filtrosAtivos) { fStatus = null; fCategoria = null; fFaixa = null }
        }
    ) { largo, grande ->
        if (d.ativos.isEmpty()) {
            SemDadosDash("Sem ativos", "Nenhum ativo cadastrado.")
            return@PainelDashboard
        }
        PaineisDoDash("ativos", largo, organizar = !grande, itens = listOf(
            ItemPainel("kpis", "Indicadores", inteiro = true, conteudo = {
                GradeKpis(
            largo,
            listOf(
                { m -> KpiDash("Ativos no período", FmtDash.qtd(lista.size), m, legenda = "${lista.count { it.emPosse }} em posse hoje") },
                { m -> KpiDash("Chegadas", FmtDash.qtd(chegadas), m, cor = CorStatusAtivo.getValue("DISPONIVEL"), legenda = "no período") },
                { m -> KpiDash("Devoluções / baixas", FmtDash.qtd(saidas), m, cor = CorStatusAtivo.getValue("DEVOLVIDO"), legenda = "no período") },
                { m -> KpiDash("Em uso hoje", FmtDash.qtd(lista.count { it.status == "EM_USO" }), m, cor = CorStatusAtivo.getValue("EM_USO")) },
                { m -> KpiDash("Disponíveis hoje", FmtDash.qtd(lista.count { it.status == "DISPONIVEL" }), m, cor = CorStatusAtivo.getValue("DISPONIVEL")) },
                { m ->
                    val n = lista.count { it.status == "MANUTENCAO" }
                    KpiDash("Em manutenção hoje", FmtDash.qtd(n), m, cor = CorStatusAtivo.getValue("MANUTENCAO"), destaque = n > 0)
                },
                { m ->
                    KpiDash(
                        "Custo de aluguel no período", FmtDash.moeda(custoPeriodo), m, cor = PortalCores.Navy,
                        legenda = "atual: ${FmtDash.moeda(custoMesAtual)}/mês"
                    )
                },
                { m ->
                    KpiDash(
                        "Solicitações no período", FmtDash.qtd(solicNoPeriodo.size), m,
                        cor = if (solicAbertas > 0) CoresDash.atencao else CoresDash.bom, legenda = "$solicAbertas em aberto"
                    )
                }
            )
        )
            }),
            ItemPainel("status", "Por status") { m ->
                GraficoRosca(
                    "Por status", "Ativos do período, situação atual · clique para filtrar",
                    contagem(daObra.filter { fCategoria == null || categoria(it) == fCategoria }) { it.status }
                        .map { (s, n) -> FatiaDash(AtivoStatus.rotulo(s), n, CorStatusAtivo[s] ?: CoresDash.neutro) },
                    m, grande, rotuloTotal = "ativos", selecionada = fStatus, onClick = { fStatus = alternar(fStatus, it) }
                )
            },
            ItemPainel("propriedade", "Propriedade") { m ->
                GraficoRosca(
                    "Propriedade", "Alugados x próprios no período",
                    contagem(lista) { if (it.alugado) "Alugado" else it.propriedade.lowercase().replaceFirstChar { c -> c.uppercase() }.ifBlank { "Próprio" } }
                        .mapIndexed { i, (k, n) -> FatiaDash(k, n, if (k == "Alugado") PortalCores.Laranja else CoresDash.cor(i)) },
                    m, grande, rotuloTotal = "ativos"
                )
            },
            ItemPainel("obra", "Ativos por obra") { m ->
                RankingBarras(
                    "Ativos por obra", "No período · clique para filtrar a obra",
                    contagem(presentes) { nomeCc(it.centroCustoId) }, m,
                    selecionado = if (cc.isBlank()) null else nomeCc(cc),
                    onClick = { r -> val id = d.centrosCusto.firstOrNull { it.rotulo == r }?.id.orEmpty(); cc = if (cc == id) "" else id }
                )
            },
            ItemPainel("custo-obra", "Custo de aluguel por obra") { m ->
                RankingBarras(
                    "Custo de aluguel por obra", "Proporcional aos dias de aluguel no período",
                    soma(presentes.filter { it.alugado }, { nomeCc(it.centroCustoId) }, ::custoNoPeriodo).filter { it.second > 0 }, m,
                    cor = PortalCores.Laranja, fmt = { FmtDash.moeda(it) }
                )
            },
            ItemPainel("movimentos", "Movimentações por período", inteiro = true) { m ->
                GraficoColunas(
            "Movimentações por período", "Chegadas, transferências, manutenção, devoluções e baixas · clique para filtrar",
            faixas.map { it.rotulo },
            seriesPorChave(movs, faixas, ::dataMov, { MovimentacaoTipo.rotulo(it.tipo) }, coresMov),
            m, grande, fmt = { FmtDash.qtd(it) },
            selecionada = fFaixa, onClick = { fFaixa = if (fFaixa == it) null else it }
                )
            },
            ItemPainel("categoria", "Por categoria") { m ->
                RankingBarras("Por categoria", "No período · clique para filtrar", contagem(lista, ::categoria), m, cor = CoresDash.cor(3), selecionado = fCategoria, onClick = { fCategoria = alternar(fCategoria, it) })
            },
            ItemPainel("tipos-mov", "Tipos de movimentação") { m ->
                GraficoRosca(
                    "Tipos de movimentação", if (fFaixa != null) "No período selecionado" else "No período",
                    fatias(contagem(movsFiltradas) { MovimentacaoTipo.rotulo(it.tipo) }, coresMov), m, grande, rotuloTotal = "movim."
                )
            }
        ))
    }
}
